import { FC, useState } from 'react'
import { Tooltip } from '@lib/tooltip'
import { IconButton, MenuButton } from 'components/buttons'
import {
  faEllipsisV,
  faThumbTack as faThumbTackSolid,
} from '@fortawesome/pro-solid-svg-icons'
import {
  useDeselectTask,
  usePinnedTask,
  usePinTask,
  useUnpinTask,
} from '../../../hooks/useTasks'
import { useTranslation } from 'react-i18next'
import { faThumbTack } from '@fortawesome/pro-regular-svg-icons'
import { twJoin } from 'tailwind-merge'
import { Spinner } from 'components/loaders'
import {
  Row,
  TwoColumnTinyTable,
} from 'components/tables/two-column-tiny-table'
import {
  isJiraTaskSourceDetails,
  isShotgridTaskSourceDetails,
} from 'utils/sourceDetailsTypeGuards'
import { DateTime } from 'luxon'

interface Props {
  task: SelectedTask
  taskIdsWithEntries: Set<number>
  start: DateTime
  end: DateTime
}

export const TaskCell: FC<Props> = ({
  task,
  taskIdsWithEntries,
  start,
  end,
}) => {
  const { t } = useTranslation()
  const pathString = task.displayPath.join(' • ')
  const deselectTask = useDeselectTask({ start, end })
  const pinTask = usePinTask()
  const unpinTask = useUnpinTask()
  const [isMouseOver, setIsMouseOver] = useState(false)
  const [dropdownIsHidden, setDropdownIsHidden] = useState(true)

  const removeTaskIsDisabled = taskIdsWithEntries.has(task.id)
  const pinnedTaskQuery = usePinnedTask(task.id)
  const isPinned = Boolean(pinnedTaskQuery.data)
  const isMutatingPin = pinTask.isLoading || unpinTask.isLoading

  const tooltip = removeTaskIsDisabled
    ? t(
        'features.timeLogging.taskSelection.taskCannotBeRemovedWhenTimeIsAllocatedToIt',
      )
    : undefined

  return (
    <div
      className="relative grow grid grid-cols-2 gap-2 text-xs group"
      onMouseOver={() => setIsMouseOver(true)}
      onMouseLeave={() => setIsMouseOver(false)}
    >
      <div
        className={twJoin(
          'absolute left-[-25px] top-1/2 -translate-y-1/2 z-20',
          isPinned || isMutatingPin
            ? 'opacity-100'
            : 'opacity-0 group-hover:opacity-100',
        )}
      >
        {isMutatingPin || pinnedTaskQuery.isLoading ? (
          <div className="w-6 h-6 flex items-center justify-center">
            <Spinner />
          </div>
        ) : (
          <IconButton
            className={twJoin('bg-white', !isPinned && '-rotate-45')}
            icon={isPinned ? faThumbTackSolid : faThumbTack}
            onClick={() =>
              isPinned ? unpinTask.mutate(task.id) : pinTask.mutate(task.id)
            }
            disabled={pinnedTaskQuery.isLoading}
            tooltip={
              isPinned
                ? undefined
                : t(
                    'features.timeLogging.taskSelection.pinnedTasksAreCopiedOverWeekToWeek',
                  )
            }
            tooltipProps={{ delayShow: 1000 }}
            circle={true}
          />
        )}
      </div>
      <div className="flex flex-row items-center font-medium text-neutral-600 pl-2">
        <span
          className="text-ellipsis overflow-hidden whitespace-nowrap"
          data-tooltip-id={`selected-task-cell-task-path-${task.id}`}
        >
          <Tooltip
            id={`selected-task-cell-task-path-${task.id}`}
            delayShow={1000}
          >
            {pathString}
          </Tooltip>

          {pathString}
        </span>
      </div>
      <div className="flex flex-row items-center justify-between font-semibold text-neutral-900 pr-2">
        <span
          className="text-ellipsis overflow-hidden whitespace-nowrap"
          data-tooltip-id={`selected-task-cell-task-name-${task.id}`}
        >
          <Tooltip
            id={`selected-task-cell-task-name-${task.id}`}
            delayShow={1000}
          >
            {task.name}
          </Tooltip>
          {task.name}
        </span>
        {(isMouseOver || !dropdownIsHidden) && (
          <MenuButton
            button={({ disabled, toggleMenuHidden }) => (
              <IconButton
                icon={faEllipsisV}
                onClick={toggleMenuHidden}
                disabled={disabled}
              />
            )}
            menuItems={[
              {
                as: 'div',
                content: (
                  <div className="flex flex-col bg-neutral-100 px-4 pt-2 w-[243px]">
                    <span className="text-xs font-medium text-neutral-900 px-2">
                      {task.name}
                    </span>
                    <TwoColumnTinyTable>
                      {isShotgridTaskSourceDetails(task.sourceDetails) && (
                        <Row
                          label={t('common.shotgridId')}
                          value={task.sourceDetails.shotgridId}
                        />
                        // TODO: SG/Flow PT status
                      )}
                      {isJiraTaskSourceDetails(task.sourceDetails) && (
                        <>
                          <Row
                            label={t('common.jiraId')}
                            value={task.sourceDetails.jiraId}
                          />
                          <Row
                            label={t('common.summary')}
                            value={task.sourceDetails.summary}
                          />
                        </>
                      )}
                    </TwoColumnTinyTable>
                  </div>
                ),
              },
              {
                label: t('features.timeLogging.taskSelection.removeTask'),
                onClick: () => deselectTask.mutate(task),
                disabled: removeTaskIsDisabled,
                tooltip,
              },
              {
                label: isPinned
                  ? t('features.timeLogging.taskSelection.unpinTask')
                  : t('features.timeLogging.taskSelection.pinTask'),
                onClick: () =>
                  isPinned
                    ? unpinTask.mutate(task.id)
                    : pinTask.mutate(task.id),
                loading: pinnedTaskQuery.isLoading,
                disabled: pinnedTaskQuery.isLoading,
                tooltip: isPinned
                  ? null
                  : t(
                      'features.timeLogging.taskSelection.pinnedTasksAreCopiedOverWeekToWeek',
                    ),
                tooltipProps: { delayShow: 1000, place: 'right' },
              },
            ]}
            onMenuHiddenChange={setDropdownIsHidden}
          />
        )}
      </div>
    </div>
  )
}
